/**
 * Cold and electricity (GDD sections 6, 8 and "Synergies").
 *
 * Freezing (cryo-grenade, ice mine, Snowman): water and steam become ice, lava sets into obsidian, fire goes
 * out. Units hit are frozen: they cannot be played at the next turn of their team, except Lavaman and
 * Fireman; the cold heals Snowman.
 *
 * Electricity is not a cell: a discharge (Robotman's carcass) runs at once through the connected water,
 * deals its damage once to each unit it reaches, then disappears: nothing stays electrified.
 */
import { Physique, isqrt, wakeRect } from '@invaders/sim';
import { FP_SHIFT, FROZEN_PENDING, type GameState, type Unit } from './state';
import { INVADERS } from './invaders';
import { UNIT_H, UNIT_W, damageUnit, healUnit, spared } from './unit';
import { freezeSurge } from './surge';
import { GEYSER_HEIGHT } from './arsenal';

const { EMPTY, WATER, STEAM, LAVA, OBSIDIAN, FIRE, ICE } = Physique;

/** Distance from (x, y) to the nearest cell of the unit's body. */
export function distanceToBody(u: Unit, x: number, y: number): number {
  const ux = u.x >> FP_SHIFT;
  const uy = u.y >> FP_SHIFT;
  const dx = x < ux ? ux - x : x > ux + UNIT_W - 1 ? x - (ux + UNIT_W - 1) : 0;
  const dy = y < uy ? uy - y : y > uy + UNIT_H - 1 ? y - (uy + UNIT_H - 1) : 0;
  return isqrt(dx * dx + dy * dy);
}

/** Water and steam to ice, lava to obsidian, fire out, in a disc. */
export function freezeCells(state: GameState, cx: number, cy: number, r: number): void {
  const { w, h, data } = state.world.physique;
  const cells = state.world.cellState.data;
  for (let y = Math.max(0, cy - r); y <= Math.min(h - 1, cy + r); y++) {
    for (let x = Math.max(0, cx - r); x <= Math.min(w - 1, cx + r); x++) {
      if ((x - cx) * (x - cx) + (y - cy) * (y - cy) > r * r) continue;
      const i = y * w + x;
      const m = data[i];
      const to = m === WATER || m === STEAM ? ICE : m === LAVA ? OBSIDIAN : m === FIRE ? EMPTY : -1;
      if (to >= 0) { data[i] = to; cells[i] = 0; }
    }
  }
  wakeRect(state.world, cx - r, cy - r, cx + r, cy + r);
}

/** Freezes a unit (in a match; the sandbox has no turns to skip), unless the cold does not touch it. */
export function freezeUnit(state: GameState, u: Unit, damage: number): void {
  const effect = INVADERS[u.kind].freeze;
  if (effect === 'immune') return;
  if (effect === 'healed') { healUnit(u, damage); return; }
  if (damage > 0) damageUnit(state, u, damage, 'direct');
  u.burning = false;
  if (state.match && u.alive) u.frozen = FROZEN_PENDING;
}

/**
 * A blast of cold of radius r at (cx, cy): cells and units. Synergies: a Wave inside it sets into a wall of
 * ice ("Vague figée"), a geyser freezes into a tower of ice ("Pilier de glace").
 */
export function freezeArea(state: GameState, cx: number, cy: number, r: number, damage: number): void {
  for (const s of [...state.surges]) {
    const dx = s.x - cx;
    const dy = s.floor - (s.height >> 1) - cy;
    if (dx * dx + dy * dy <= (r + (s.height >> 1)) * (r + (s.height >> 1))) freezeSurge(state, s);
  }
  for (let k = state.geysers.length - 1; k >= 0; k--) {
    const g = state.geysers[k];
    if (g.wait === 0 && Math.abs(g.x - cx) <= r && cy >= g.y - GEYSER_HEIGHT - r && cy <= g.y + r) {
      // The column of water freezes on its whole height.
      for (let y = g.y - GEYSER_HEIGHT; y < g.y; y += 2 * r) freezeCells(state, g.x, y + r, r);
      freezeCells(state, g.x, g.y - GEYSER_HEIGHT, r);
      state.geysers.splice(k, 1);
    }
  }
  freezeCells(state, cx, cy, r);
  for (const u of state.units) if (u.alive && distanceToBody(u, cx, cy) <= r && !spared(state, u)) freezeUnit(state, u, damage);
  state.events.push({ type: 'freeze', x: cx, y: cy, radius: r });
}

/** Cells of connected water a discharge may run through, at most. */
const MAX_DISCHARGE_CELLS = 40000;

/**
 * An electric discharge of radius r at (cx, cy): every unit within r, every unit touching water that is
 * connected to water within r, and those `also` designates, take `damage` once. Sparks set the black powder
 * and oil within r on fire.
 */
export function discharge(state: GameState, cx: number, cy: number, r: number, damage: number, also?: (u: Unit) => boolean): void {
  const { w, h, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const seen = new Uint8Array(w * h);
  const queue: number[] = [];
  for (let y = Math.max(0, cy - r); y <= Math.min(h - 1, cy + r); y++) {
    for (let x = Math.max(0, cx - r); x <= Math.min(w - 1, cx + r); x++) {
      if ((x - cx) * (x - cx) + (y - cy) * (y - cy) > r * r) continue;
      const i = y * w + x;
      if (data[i] === WATER && !seen[i]) { seen[i] = 1; queue.push(i); }
      else if (data[i] === Physique.BLACK_POWDER || data[i] === Physique.OIL) { data[i] = FIRE; cells[i] = 0; }
    }
  }
  for (let k = 0; k < queue.length && queue.length < MAX_DISCHARGE_CELLS; k++) {
    const i = queue[k];
    const x = i % w;
    for (const n of [i - 1, i + 1, i - w, i + w]) {
      if (n < 0 || n >= w * h || seen[n] || (n === i - 1 && x === 0) || (n === i + 1 && x === w - 1)) continue;
      if (data[n] === WATER) { seen[n] = 1; queue.push(n); }
    }
  }
  wakeRect(state.world, cx - r, cy - r, cx + r, cy + r);

  for (const u of state.units) {
    if (!u.alive) continue;
    let hit = distanceToBody(u, cx, cy) <= r || (also !== undefined && also(u));
    if (!hit && queue.length > 0) {
      const ux = u.x >> FP_SHIFT;
      const uy = u.y >> FP_SHIFT;
      for (let y = Math.max(0, uy - 1); y <= Math.min(h - 1, uy + UNIT_H) && !hit; y++) {
        for (let x = Math.max(0, ux - 1); x <= Math.min(w - 1, ux + UNIT_W); x++) if (seen[y * w + x]) { hit = true; break; }
      }
    }
    if (hit) damageUnit(state, u, damage, 'direct');
  }
  // A sample of the electrified cells for the client's sparks.
  const sample: number[] = [];
  const step = Math.max(1, Math.trunc(queue.length / 150));
  for (let k = 0; k < queue.length; k += step) sample.push(queue[k] % w, Math.trunc(queue[k] / w));
  state.events.push({ type: 'discharge', x: cx, y: cy, radius: r, cells: sample });
}

